import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { format } from 'date-fns'
import { FileText, Plus, Search, ChevronRight } from 'lucide-react'

const STATUSES = ['all', 'draft', 'sent', 'accepted', 'declined', 'converted']

function statusLabel(s) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function paymentBadge(status) {
  const map = {
    pending: 'badge-pending',
    deposit_paid: 'badge-deposit_paid',
    paid_in_full: 'badge-paid_in_full',
    overdue: 'badge-overdue',
  }
  const labels = {
    pending: 'Pending',
    deposit_paid: 'Deposit Paid',
    paid_in_full: 'Paid in Full',
    overdue: 'Overdue',
  }
  return { cls: map[status] || 'badge-pending', label: labels[status] || status }
}

export default function QuotesPage() {
  const navigate = useNavigate()
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const loadQuotes = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const { data, error } = await supabase
        .from('quotes')
        .select('*, clients(name, email)')
        .order('created_at', { ascending: false })

      if (error) throw error
      setQuotes(data || [])
    } catch (err) {
      console.error('Failed to load quotes:', err)
      setError(err?.message || 'Failed to load quotes.')
      setQuotes([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadQuotes()
  }, [loadQuotes])

  const filtered = quotes.filter(q => {
    const matchStatus = statusFilter === 'all' || q.status === statusFilter
    const term = search.toLowerCase()
    const matchSearch =
      !term ||
      q.quote_number?.toLowerCase().includes(term) ||
      q.clients?.name?.toLowerCase().includes(term) ||
      q.clients?.email?.toLowerCase().includes(term) ||
      q.event_type?.toLowerCase().includes(term)
    return matchStatus && matchSearch
  })

  // Stats
  const byStatus = {}
  STATUSES.slice(1).forEach(s => { byStatus[s] = quotes.filter(q => q.status === s).length })
  const totalValue = quotes
    .filter(q => ['accepted', 'converted'].includes(q.status))
    .reduce((sum, q) => sum + parseFloat(q.grand_total || 0), 0)

  return (
    <div data-testid="quotes-section">
      {error && <div className="alert alert-error" data-testid="feedback-banner-error" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Quotes</h1>
          <p className="page-subtitle">Build and manage client quotes</p>
        </div>
        <button data-testid="create-quote-button" className="btn btn-primary" onClick={() => navigate('/quotes/new')}>
          <Plus size={15} /> Create Quote
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 'var(--sp-5)' }}>
        <div className="stat-card">
          <div className="stat-label">Total Quotes</div>
          <div className="stat-value">{quotes.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Draft</div>
          <div className="stat-value">{byStatus.draft || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Sent</div>
          <div className="stat-value">{byStatus.sent || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Accepted</div>
          <div className="stat-value" style={{ color: 'var(--color-green)' }}>{byStatus.accepted || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Accepted Value</div>
          <div className="stat-value" style={{ fontSize: 'var(--text-xl)' }}>
            ${totalValue.toLocaleString('en-CA', { minimumFractionDigits: 0 })}
          </div>
          <div className="stat-sub">accepted + converted</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 'var(--sp-4)', flexWrap: 'wrap' }}>
        {/* Status tabs */}
        <div style={{ display: 'flex', gap: 2, background: 'var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 2 }}>
          {STATUSES.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              style={{
                padding: '4px 12px', border: 'none', cursor: 'pointer',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-body)', fontSize: 'var(--text-xs)', fontWeight: 500,
                background: statusFilter === s ? 'white' : 'transparent',
                color: statusFilter === s ? 'var(--color-text)' : 'var(--color-text-muted)',
                boxShadow: statusFilter === s ? 'var(--shadow-sm)' : 'none',
                transition: 'all 0.12s',
              }}
            >
              {statusLabel(s)}
              {s !== 'all' && byStatus[s] > 0 && (
                <span style={{ marginLeft: 5, background: 'var(--color-border)', borderRadius: 10, padding: '1px 5px', fontSize: 10 }}>
                  {byStatus[s]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="search-bar" style={{ marginLeft: 'auto' }}>
          <Search size={14} className="search-icon" />
          <input
            placeholder="Search quote #, client…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Quotes Table */}
      <div className="card">
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading quotes…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state" data-testid="empty-state-quotes-list">
            <FileText size={36} className="empty-state-icon" />
            <p style={{ fontWeight: 600 }}>No quotes found</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              {search || statusFilter !== 'all'
                ? 'No quotes match your current filters. Clear filters to see more.'
                : 'Create your first quote to start building pipeline.'}
            </p>
            {search || statusFilter !== 'all' ? (
              <button className="btn btn-secondary" onClick={() => { setSearch(''); setStatusFilter('all') }}>
                Clear Filters
              </button>
            ) : (
              <button className="btn btn-primary" onClick={() => navigate('/quotes/new')}>
                <Plus size={14} /> Create Quote
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Quote #</th>
                  <th>Client</th>
                  <th>Event Date</th>
                  <th>Event Type</th>
                  <th>Status</th>
                  <th>Grand Total</th>
                  <th>Payment</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(q => {
                  const { cls: pCls, label: pLabel } = paymentBadge(q.payment_status)
                  return (
                    <tr
                      key={q.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/quotes/${q.id}`)}
                    >
                      <td>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-navy)' }}>
                          {q.quote_number || '—'}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{q.clients?.name || '—'}</div>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{q.clients?.email || ''}</div>
                      </td>
                      <td style={{ fontSize: 'var(--text-sm)', whiteSpace: 'nowrap' }}>
                        {q.event_date
                          ? format(new Date(q.event_date + 'T12:00:00'), 'MMM d, yyyy')
                          : '—'}
                      </td>
                      <td style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', textTransform: 'capitalize' }}>
                        {q.event_type || '—'}
                      </td>
                      <td>
                        <span className={`badge badge-${q.status}`}>{statusLabel(q.status)}</span>
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 'var(--text-sm)' }}>
                        ${parseFloat(q.grand_total || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td>
                        <span className={`badge ${pCls}`}>{pLabel}</span>
                      </td>
                      <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                        {q.created_at ? format(new Date(q.created_at), 'MMM d') : '—'}
                      </td>
                      <td>
                        <ChevronRight size={14} style={{ color: 'var(--color-text-muted)' }} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
